import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, Linkedin, Instagram, Facebook } from "lucide-react";
import { COMPANY, NAV, SOCIALS } from "@/lib/constants";

// Custom TikTok icon (lucide doesn't include it)
const TikTokIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.74a8.16 8.16 0 0 0 4.77 1.52V6.85a4.85 4.85 0 0 1-1.84-.16z"/>
  </svg>
);

const ICONS = { Linkedin, Instagram, Facebook, TikTok: TikTokIcon };

export default function Footer() {
  return (
    <footer
      className="bg-[#0A1F47] text-white pt-20 pb-8 relative overflow-hidden"
      data-testid="site-footer"
    >
      <div className="absolute inset-0 grid-overlay opacity-20 pointer-events-none" aria-hidden />
      {/* Soft accent glow */}
      <div className="absolute -top-32 -left-32 w-[420px] h-[420px] rounded-full bg-[#A6CD3C]/8 blur-3xl pointer-events-none" aria-hidden />
      <div className="absolute -bottom-40 right-[-80px] w-[380px] h-[380px] rounded-full bg-[#3B6CA8]/10 blur-3xl pointer-events-none" aria-hidden />

      <div className="container-x relative z-10">
        <div className="grid lg:grid-cols-12 gap-x-12 gap-y-14 pb-14">
          {/* ── Brand column ────────────────────────────────────────── */}
          <div className="lg:col-span-5">
            <Link to="/" className="inline-flex items-center mb-6" data-testid="footer-brand-link">
              <img
                src={COMPANY.logo}
                alt="ISGS Consulting Group — Asbestos Consulting"
                className="h-24 w-auto object-contain rounded-md"
              />
            </Link>

            <p className="text-white/65 text-[14px] leading-relaxed max-w-md mb-8">
              NSW-based specialists in asbestos identification, risk assessment, air monitoring and
              remediation project management — backed by 18+ years of field expertise.
            </p>

            {/* Social — clickable icons, ready for real URLs */}
            <div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#A6CD3C] font-bold mb-4">
                Follow ISGS
              </div>
              <div className="flex items-center gap-3" data-testid="footer-socials">
                {SOCIALS.map((s) => {
                  const Icon = ICONS[s.icon];
                  return (
                    <a
                      key={s.name}
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`ISGS on ${s.name}`}
                      title={s.name}
                      className="group w-11 h-11 rounded-full bg-white/[0.06] border border-white/15 text-white/85 hover:bg-[#A6CD3C] hover:border-[#A6CD3C] hover:text-[#0A1F47] hover:-translate-y-0.5 flex items-center justify-center transition-all duration-200"
                      data-testid={`social-${s.name.toLowerCase()}`}
                    >
                      <Icon size={18} />
                    </a>
                  );
                })}
              </div>
            </div>
          </div>

          {/* ── Navigate ────────────────────────────────────────────── */}
          <nav className="lg:col-span-3" aria-label="Footer navigation">
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#A6CD3C] font-bold mb-5">
              Navigate
            </div>
            <ul className="space-y-3">
              {NAV.map((n) => (
                <li key={n.to}>
                  <Link
                    to={n.to}
                    className="text-white/75 hover:text-[#A6CD3C] transition-colors text-[14px]"
                    data-testid={`footer-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {n.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* ── Contact ─────────────────────────────────────────────── */}
          <div className="lg:col-span-4">
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#A6CD3C] font-bold mb-5">
              Get in Touch
            </div>

            {/* Emergency phone card */}
            <a
              href={`tel:${COMPANY.phoneAltTel}`}
              className="block rounded-xl bg-white/[0.05] border border-white/12 hover:border-[#A6CD3C]/60 px-4 py-3.5 mb-3 transition-colors"
              data-testid="footer-emergency-phone"
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-[#A6CD3C]/15 border border-[#A6CD3C]/40 flex items-center justify-center">
                  <Phone size={15} className="text-[#A6CD3C]" />
                </div>
                <div>
                  <div className="text-[9.5px] uppercase tracking-[0.24em] text-[#A6CD3C] font-bold">
                    24/7 Emergency
                  </div>
                  <div className="text-[15.5px] font-semibold text-white">{COMPANY.phoneAlt}</div>
                </div>
              </div>
            </a>

            {/* Compact contact list */}
            <ul className="space-y-3 text-[13.5px]">
              <li>
                <a
                  href={`tel:${COMPANY.phoneTel}`}
                  className="flex items-center gap-3 text-white/75 hover:text-[#A6CD3C] transition-colors"
                  data-testid="footer-direct-phone"
                >
                  <Phone size={14} className="text-white/50 shrink-0" />
                  <span>
                    <span className="text-white/50 text-[11px] uppercase tracking-[0.18em] mr-2">Direct</span>
                    {COMPANY.phone}
                  </span>
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${COMPANY.email}`}
                  className="flex items-center gap-3 text-white/75 hover:text-[#A6CD3C] transition-colors break-all"
                  data-testid="footer-email"
                >
                  <Mail size={14} className="text-white/50 shrink-0" />
                  <span>{COMPANY.email}</span>
                </a>
              </li>
              <li className="flex items-start gap-3 text-white/75">
                <MapPin size={14} className="text-white/50 shrink-0 mt-0.5" />
                <span>{COMPANY.address}</span>
              </li>
              <li className="flex items-start gap-3 text-white/75">
                <Clock size={14} className="text-white/50 shrink-0 mt-0.5" />
                <span>{COMPANY.hours}</span>
              </li>
            </ul>
          </div>
        </div>

        {/* ── Bottom bar ───────────────────────────────────────────── */}
        <div className="border-t border-white/10 pt-6 space-y-3">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 text-[12px] text-white/50">
            <div data-testid="footer-copyright">
              © {new Date().getFullYear()} {COMPANY.legalName}. All rights reserved.
            </div>
          </div>
          <p
            className="text-[10.5px] leading-relaxed text-white/40 max-w-4xl"
            data-testid="footer-disclaimer"
          >
            <span className="font-semibold text-white/55">Disclaimer:</span> Images on this website
            are sourced from ISGS Consulting Group Pty Ltd, government publications, publicly
            available sources, and AI-assisted content. Copyright remains with the respective
            owners where applicable.
          </p>
        </div>
      </div>
    </footer>
  );
}
